import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
    Trophy, Bicycle, Fire, MapPin, Globe, Medal,
    Lightning, Gift, Target, Crown, ShoppingCart,
    Wallet, Star, Diamond, CheckCircle, Lock
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_MAP = {
    bicycle: Bicycle, fire: Fire, trophy: Trophy, map: MapPin,
    globe: Globe, medal: Medal, lightning: Lightning, gift: Gift,
    target: Target, crown: Crown, cart: ShoppingCart, wallet: Wallet,
    star: Star, diamond: Diamond,
};

const RARITY_STYLES = {
    common: { border: 'border-white/10', bg: 'bg-white/5', text: 'text-white/60', label: 'Common' },
    uncommon: { border: 'border-neon-green/30', bg: 'bg-neon-green/5', text: 'text-neon-green', label: 'Uncommon' },
    rare: { border: 'border-primary/30', bg: 'bg-primary/5', text: 'text-primary', label: 'Rare' },
    epic: { border: 'border-hot-pink/30', bg: 'bg-hot-pink/5', text: 'text-hot-pink', label: 'Epic' },
    legendary: { border: 'border-neon-gold/30', bg: 'bg-neon-gold/5', text: 'text-neon-gold', label: 'Legendary' },
};

export default function Achievements() {
    const { updateUser } = useAuth();
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(null);

    useEffect(() => { fetchAchievements(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAchievements = async () => {
        try {
            const res = await fetch(`${API}/achievements`, { credentials: 'include' });
            if (res.ok) setAchievements(await res.json());
        } catch (error) {
            console.error('Achievements fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const claimAchievement = async (id) => {
        setClaiming(id);
        try {
            const res = await fetch(`${API}/achievements/${id}/claim`, {
                method: 'POST', credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`${data.achievement} unlocked! +${data.boltz_earned} Boltz`);
                updateUser({ boltz_balance: data.new_balance });
                fetchAchievements();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to claim');
            }
        } catch (error) {
            toast.error('Claim failed');
        } finally {
            setClaiming(null);
        }
    };

    const unlocked = achievements.filter(a => a.progress >= a.requirement);
    const locked = achievements.filter(a => a.progress < a.requirement);

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <span className="label-text text-primary text-glow">ACHIEVEMENTS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">NFT Badges</h1>
                    <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                        Earn on-chain achievement NFTs for your riding milestones
                    </p>
                    <div className="flex gap-4 mt-4">
                        <div className="text-center">
                            <p className="text-2xl font-black font-display text-neon-gold text-glow-gold" data-testid="unlocked-count">
                                {unlocked.length}
                            </p>
                            <p className="label-text text-neon-gold">UNLOCKED</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-black font-display text-muted-foreground">{achievements.length}</p>
                            <p className="label-text">TOTAL</p>
                        </div>
                    </div>
                </motion.div>

                {loading ? (
                    <div className="space-y-4">
                        {[1,2,3].map(i => <Card key={i} className="glass-card"><CardContent className="p-4"><Skeleton className="h-24 w-full" /></CardContent></Card>)}
                    </div>
                ) : (
                    <>
                        {/* Unlocked */}
                        {unlocked.length > 0 && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
                                <h2 className="font-bold text-sm font-display tracking-wider text-neon-green mb-3 flex items-center gap-2">
                                    <CheckCircle size={16} weight="fill" /> READY TO CLAIM
                                </h2>
                                <div className="space-y-3">
                                    {unlocked.map((ach, i) => {
                                        const Icon = ICON_MAP[ach.icon] || Trophy;
                                        const rarity = RARITY_STYLES[ach.rarity] || RARITY_STYLES.common;
                                        return (
                                            <motion.div
                                                key={ach.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                            >
                                                <Card className={`glass-card ${rarity.border} ${ach.claimed ? 'opacity-70' : 'shadow-[0_0_15px_rgba(0,230,77,0.1)]'}`}>
                                                    <CardContent className="p-4 flex items-center gap-4">
                                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${rarity.bg} shrink-0`}>
                                                            <Icon size={28} className={rarity.text} weight="fill" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="font-bold text-sm truncate">{ach.title}</h3>
                                                                <Badge className={`${rarity.bg} ${rarity.text} text-[10px] border ${rarity.border}`}>{rarity.label}</Badge>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground mt-0.5">{ach.description}</p>
                                                            <p className="text-xs font-mono font-bold text-neon-gold mt-1">+{ach.boltz_reward} Boltz</p>
                                                        </div>
                                                        {ach.claimed ? (
                                                            <Badge variant="outline" className="text-neon-green border-neon-green/30 shrink-0">
                                                                <CheckCircle size={12} className="mr-1" />Claimed
                                                            </Badge>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => claimAchievement(ach.id)}
                                                                disabled={claiming === ach.id}
                                                                className="bg-neon-green hover:bg-neon-green/80 text-black font-display text-xs shrink-0"
                                                                data-testid={`claim-${ach.id}`}
                                                            >
                                                                {claiming === ach.id ? 'Claiming...' : 'Claim'}
                                                            </Button>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {/* Locked */}
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                            <h2 className="font-bold text-sm font-display tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                                <Lock size={16} /> LOCKED
                            </h2>
                            <div className="space-y-3">
                                {locked.map((ach, i) => {
                                    const Icon = ICON_MAP[ach.icon] || Trophy;
                                    const rarity = RARITY_STYLES[ach.rarity] || RARITY_STYLES.common;
                                    const pct = Math.min(100, (ach.progress / ach.requirement) * 100);
                                    return (
                                        <motion.div
                                            key={ach.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 + i * 0.05 }}
                                        >
                                            <Card className="glass-card opacity-60 hover:opacity-80 transition-opacity">
                                                <CardContent className="p-4 flex items-center gap-4">
                                                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white/5 shrink-0">
                                                        <Icon size={28} className="text-muted-foreground" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-bold text-sm truncate">{ach.title}</h3>
                                                            <Badge variant="outline" className="text-[10px] border-white/10">{rarity.label}</Badge>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mt-0.5">{ach.description}</p>
                                                        <div className="mt-2">
                                                            <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                                                                <span>{typeof ach.progress === 'number' ? ach.progress.toFixed(1) : ach.progress} / {ach.requirement}</span>
                                                                <span>{pct.toFixed(0)}%</span>
                                                            </div>
                                                            <Progress value={pct} className="h-1.5" />
                                                        </div>
                                                    </div>
                                                    <span className="text-xs font-mono text-muted-foreground shrink-0">+{ach.boltz_reward}</span>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </>
                )}
            </div>
        </Layout>
    );
}
